/**
 * Peças de exibição de dado que se repetem nas cinco telas.
 *
 * Não são "estilo": cada uma carrega uma decisão de leitura. A lista de
 * motivos sempre mostra o texto que a API devolveu, nunca só o sinal — cor
 * sozinha não é veredito auditável, e é exatamente o que se cobra depois.
 */
import type { ReactNode } from "react";
import type { Pagina } from "@co/contracts";
import { Botao } from "./base.js";
import { num } from "./formato.js";

export function Rotulo({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-[10.5px] uppercase tracking-[0.08em] text-tinta-3 font-semibold">
      {children}
    </div>
  );
}

export function Definicoes({
  itens,
}: {
  itens: Array<{ k: string; v: ReactNode }>;
}): JSX.Element {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px] items-baseline">
      {itens.map((i) => (
        <div key={i.k} className="contents">
          <dt className="text-[10.5px] uppercase tracking-[0.06em] text-tinta-3 font-semibold">
            {i.k}
          </dt>
          <dd className="m-0 text-tinta min-w-0 break-words">{i.v}</dd>
        </div>
      ))}
    </dl>
  );
}

export type Sinal = "positivo" | "negativo" | "neutro";

const MARCA: Record<Sinal, { glifo: string; cor: string }> = {
  positivo: { glifo: "▴", cor: "text-ok" },
  negativo: { glifo: "▾", cor: "text-crit" },
  neutro: { glifo: "—", cor: "text-tinta-3" },
};

/** O motivo textual da API é o conteúdo; o sinal é só reforço visual. */
export function Motivos({
  itens,
}: {
  itens: Array<{ texto: string; sinal: Sinal }>;
}): JSX.Element {
  return (
    <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
      {itens.map((i, idx) => (
        <li
          key={`${idx}-${i.texto}`}
          className="flex gap-2 text-[12.5px] text-tinta-2 leading-snug"
        >
          <span aria-hidden="true" className={`shrink-0 ${MARCA[i.sinal].cor}`}>
            {MARCA[i.sinal].glifo}
          </span>
          <span className="min-w-0">{i.texto}</span>
        </li>
      ))}
    </ul>
  );
}

export function Chip({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-[11.5px] transition-colors ${
        ativo
          ? "bg-marca-suave border-marca text-marca-tinta font-medium"
          : "bg-superficie border-linha-forte text-tinta-2 hover:bg-superficie-3"
      }`}
    >
      {children}
    </button>
  );
}

export function LinhaVazia({
  colunas,
  children,
}: {
  colunas: number;
  children: ReactNode;
}): JSX.Element {
  return (
    <tr>
      <td
        colSpan={colunas}
        className="px-5 py-10 text-center text-[13px] text-tinta-3"
      >
        {children}
      </td>
    </tr>
  );
}

/**
 * Paginação por deslocamento. Mostra o intervalo e o total porque "página 2"
 * sozinho não responde quanto falta — e quanto falta é a pergunta real.
 */
export function Paginacao({
  pagina,
  aoIr,
}: {
  pagina: Pagina;
  aoIr: (desloc: number) => void;
}): JSX.Element | null {
  const { total, limite, desloc } = pagina;
  if (total <= limite && desloc === 0) return null;

  const inicio = total === 0 ? 0 : desloc + 1;
  const fim = Math.min(desloc + limite, total);

  return (
    <div className="flex items-center gap-3 flex-wrap px-5 py-3 border-t border-linha">
      <span className="text-[12px] text-tinta-3 tabular">
        {num(inicio)}–{num(fim)} de {num(total)}
      </span>
      <div className="ml-auto flex gap-2">
        <Botao
          pequeno
          desabilitado={desloc === 0}
          onClick={() => aoIr(Math.max(0, desloc - limite))}
        >
          Anterior
        </Botao>
        <Botao
          pequeno
          desabilitado={fim >= total}
          onClick={() => aoIr(desloc + limite)}
        >
          Próxima
        </Botao>
      </div>
    </div>
  );
}

/** Barra de filtros — some da leitura, mas não do teclado. */
export function Filtros({ children }: { children: ReactNode }): JSX.Element {
  return <div className="flex items-center gap-2 flex-wrap">{children}</div>;
}

export const classeFiltro =
  "rounded-lg border border-linha bg-superficie-2 px-2.5 py-1.5 text-[12px] text-tinta outline-none focus:border-marca placeholder:text-tinta-3";
